import type { LayoutRuntime } from "../../../../shared/layouts/registry";
import { standingsView } from "../../normalize";

type Contract = { endpoint?: string; query?: Record<string, unknown>; pollInterval?: number; initialData?: unknown };
const responseCache = new Map<string, { expiresAt: number; promise: Promise<unknown> }>();
const states = new WeakMap<HTMLElement, { activeId: string; epoch: number; timer: number }>();
const endpointFor = (contract: Contract, id: string): string => String(contract.endpoint || "").replace(/\/competitions\/[^/]+\/?$/, `/competitions/${encodeURIComponent(id)}/`);
const fetchStandings = (contract: Contract, id: string, force = false): Promise<unknown> => {
  const url = new URL(endpointFor(contract, id), window.location.origin);
  Object.entries(contract.query || {}).forEach(([key, raw]) => (Array.isArray(raw) ? raw : [raw]).forEach((entry) => url.searchParams.append(Array.isArray(raw) ? `${key}[]` : key, String(entry))));
  const key = url.toString();
  const cached = responseCache.get(key);
  if (!force && cached && cached.expiresAt > Date.now()) return cached.promise;
  const promise = fetch(url, { headers: { Accept: "application/json" } }).then(async (response) => { if (!response.ok) throw new Error(`Standings request failed: ${response.status}`); return response.json(); });
  responseCache.set(key, { expiresAt: Date.now() + 60_000, promise });
  promise.catch(() => responseCache.delete(key));
  return promise;
};

// TEMP DEBUG: remove this function and its call after inspecting competition payloads.
const logCompetitionPayload = (contract: Contract, id: string, payload: unknown) => {
  const endpoint = endpointFor(contract, id);
  console.groupCollapsed(`[GWD Standings] GET ${endpoint}`);
  console.log("competitionId:", id);
  console.log("endpoint:", endpoint);
  console.log("raw payload:", payload);
  console.groupEnd();
};

const paint = (root: HTMLElement, payload: unknown) => {
  const view = standingsView(payload);
  const content = root.querySelector<HTMLElement>("[data-gwd-standings-content]");
  const title = root.querySelector<HTMLElement>("[data-gwd-standings-title]");
  const logo = root.querySelector<HTMLElement>(".gwd-standings__competition-logo");
  if (content) content.innerHTML = view.html;
  if (title) title.textContent = view.title;
  if (logo) { logo.replaceChildren(); if (view.logo) { const image = new Image(); image.src = view.logo; image.alt = ""; image.loading = "lazy"; logo.append(image); } }
};
const skeletonHtml = (): string => `<section class="gwd-standings__group gwd-standings__skeleton" aria-hidden="true">
  <div class="gwd-standings__row is-head">${Array.from({ length: 11 }, () => "<span><i></i></span>").join("")}</div>
  ${Array.from({ length: 6 }, (_, rowIndex) => `<div class="gwd-standings__row">${Array.from({ length: 11 }, (_, columnIndex) => `<span${columnIndex === 6 || columnIndex === 7 ? ' class="is-detail"' : columnIndex >= 9 ? ' class="is-wide"' : ""}><i style="--gwd-skeleton-width:${columnIndex === 1 ? 62 + (rowIndex % 3) * 8 : columnIndex === 9 ? 76 : 40}%"></i></span>`).join("")}</div>`).join("")}
</section>`;
const load = async (root: HTMLElement, contract: Contract, id: string, force = false) => {
  const state = states.get(root); if (!state) return;
  if (!force && state.activeId === id) return;
  const epoch = ++state.epoch; state.activeId = id;
  root.querySelectorAll<HTMLElement>("[data-gwd-competition-id]").forEach((button) => button.classList.toggle("is-active", button.dataset.gwdCompetitionId === id));
  root.querySelectorAll<HTMLSelectElement>("[data-gwd-standings-active-competition]").forEach((select) => { select.value = id; });
  const card = root.querySelector<HTMLElement>(".gwd-standings__card");
  const content = root.querySelector<HTMLElement>("[data-gwd-standings-content]");
  const status = root.querySelector<HTMLElement>("[data-gwd-standings-status]");
  const previousContent = content?.innerHTML || "";
  card?.setAttribute("aria-busy", "true");
  card?.classList.toggle("is-skeleton-loading", !force);
  if (!force && content) content.innerHTML = skeletonHtml();
  if (status) status.textContent = force ? "Đang cập nhật..." : "";
  try { const payload = await fetchStandings(contract, id, force); if (states.get(root)?.epoch !== epoch) return; if (!force) logCompetitionPayload(contract, id, payload); paint(root, payload); if (status) status.textContent = ""; }
  catch (error) { if (states.get(root)?.epoch !== epoch) return; if (!force && content) content.innerHTML = previousContent; if (status) status.textContent = "Không tải được bảng xếp hạng."; console.warn("GWD standings request failed", error); }
  finally { if (states.get(root)?.epoch === epoch) { card?.setAttribute("aria-busy", "false"); card?.classList.remove("is-skeleton-loading"); } }
};

export const mountLeagueStandings = (root: HTMLElement) => {
  if (states.has(root)) return;
  const contract = JSON.parse(root.dataset.gwdContract || "{}") as Contract;
  const activeId = root.querySelector<HTMLElement>("[data-gwd-competition-id].is-active")?.dataset.gwdCompetitionId || "";
  const state = { activeId, epoch: 0, timer: 0 }; states.set(root, state);
  root.addEventListener("click", (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const competition = target?.closest<HTMLElement>("[data-gwd-competition-id]");
    if (competition?.dataset.gwdCompetitionId) void load(root, contract, competition.dataset.gwdCompetitionId);
    const details = target?.closest<HTMLButtonElement>("[data-gwd-standings-details]");
    if (details) {
      const expanded = root.classList.toggle("is-expanded");
      details.setAttribute("aria-expanded", expanded ? "true" : "false");
      details.setAttribute("aria-label", expanded ? "Thu gọn bảng xếp hạng" : "Mở rộng bảng xếp hạng");
      if (!expanded) root.querySelector<HTMLElement>("[data-gwd-standings-content]")?.scrollTo({ left: 0, behavior: "smooth" });
    }
  });
  root.querySelector<HTMLSelectElement>("[data-gwd-standings-country]")?.addEventListener("change", (event) => {
    const countryId = (event.currentTarget as HTMLSelectElement).value;
    root.querySelectorAll<HTMLElement>("[data-gwd-competition-id]").forEach((button) => { button.hidden = !!countryId && button.dataset.gwdCountryId !== countryId; });
  });
  root.querySelector<HTMLSelectElement>("[data-gwd-standings-active-competition]")?.addEventListener("change", (event) => {
    const competitionId = (event.currentTarget as HTMLSelectElement).value;
    if (competitionId) void load(root, contract, competitionId);
  });
  if (contract.initialData) paint(root, contract.initialData);
  state.timer = window.setInterval(() => state.activeId && void load(root, contract, state.activeId, true), Math.max(60_000, Number(contract.pollInterval || 900_000)));
};

export const leagueStandingsDefaultLayout: LayoutRuntime = {
  feature: "league-standings",
  layout: "default",
  rootClass: "gwd-livescore-layout--default",
  mount: mountLeagueStandings,
};
